import type { Point } from "@/components/charts/chart-primitives";
import type { ChipTone } from "@/components/shared/status-chip";
import type { CompanySummary } from "@/lib/profitability-api";

export interface BoardHero {
  trend: Point[] | null;
  /** "Dec · 55.4%": the month with the highest margin in the range. */
  bestMonth: string | null;
  /** The month with the lowest margin, same form. */
  lowestMonth: string | null;
  /** "3 of 12": months whose margin sat under the benchmark. Null without a benchmark. */
  belowBenchmark: string | null;
}

const NONE: BoardHero = { trend: null, bestMonth: null, lowestMonth: null, belowBenchmark: null };

/**
 * What the hero can show, from what the service returned.
 *
 * Pure and exported so the gating is testable without rendering the page.
 * `periods` is optional on the response today (spec 2026-10-13 §6); with it
 * absent the hero has no trend and no month facts, and says nothing about
 * them. The boards' rows are never summed into a stand-in.
 *
 * The facts are deliberately NOT revenue and cost: the five tiles directly
 * under the hero already carry those, and a hero that restates the row below
 * it is one more thing to read for nothing. Everything here comes from the
 * month series, which no tile shows.
 */
export function boardHero(data: CompanySummary): BoardHero {
  const periods = data.periods ?? [];
  const points: (Point & { hasPeriod: boolean })[] = periods.map((p) => ({
    id: p.period_id,
    label: p.period ?? p.period_id,
    value: p.margin_pct,
    hasPeriod: p.period != null,
  }));
  const present = points.filter(
    (p): p is Point & { value: number; hasPeriod: boolean } => p.value !== null,
  );
  if (present.length < 2) return NONE;
  const best = present.reduce((a, b) => (b.value > a.value ? b : a));
  const lowest = present.reduce((a, b) => (b.value < a.value ? b : a));
  // A null period leaves period_id as the label; slicing "2026-07" would give "202".
  const monthOf = (p: Point & { hasPeriod: boolean }) =>
    p.hasPeriod ? p.label.replace(/ \d{4}$/, "").slice(0, 3) : p.label;
  const benchmark = data.benchmark_pct;
  const below = benchmark === null ? null : present.filter((p) => p.value < benchmark).length;
  return {
    trend: points.map(({ id, label, value }) => ({ id, label, value })),
    bestMonth: `${monthOf(best)} · ${best.value.toFixed(1)}%`,
    lowestMonth: `${monthOf(lowest)} · ${lowest.value.toFixed(1)}%`,
    belowBenchmark: below === null ? null : `${below} of ${present.length}`,
  };
}

/**
 * How the hero reads the company margin. A loss is bad, the same as the header
 * chip says; otherwise the chip follows the benchmark gap and the figure stays
 * its default colour.
 */
export function heroTone(
  marginPct: number | null,
  gap: { direction: "below" | "above" } | null,
): { chip: ChipTone | null; figure: "default" | "bad" } {
  const loss = marginPct !== null && marginPct < 0;
  const chip: ChipTone | null =
    gap === null ? null : loss ? "bad" : gap.direction === "below" ? "warn" : "good";
  return { chip, figure: loss ? "bad" : "default" };
}
